import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import './SideBar.css';

import Typography from '../Typography';

const SideBar = ({ open, title, onClose, children }) => {
  const sideBarRef = useRef(null);

  const invokingElement = document.activeElement;

  const handledClose = () => {
    invokingElement.focus();
    onClose();
  };
  const pressEsc = (e) => {
    e.stopPropagation();
    handledClose();
  };

  const handleTabKey = (e) => {
    if (sideBarRef.current && open) {
      const focusableModalElements = sideBarRef.current.querySelectorAll(
        'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select, [tabindex = "0"]',
      );
      const firstElement = focusableModalElements[0];
      const lastElement = focusableModalElements[
        focusableModalElements.length - 1
      ].disabled
        ? focusableModalElements[focusableModalElements.length - 2]
        : focusableModalElements[focusableModalElements.length - 1];

      if (
        !Array.from(focusableModalElements).includes(document.activeElement)
      ) {
        firstElement.focus();
        return e.preventDefault();
      }
      if (!e.shiftKey && document.activeElement === lastElement) {
        firstElement.focus();
        return e.preventDefault();
      }

      if (e.shiftKey && document.activeElement === firstElement) {
        lastElement.focus();
        return e.preventDefault();
      }
    }
  };

  const keyListenersMap = new Map([
    [27, pressEsc],
    [9, handleTabKey],
  ]);

  useEffect(() => {
    const keyListener = (e) => {
      const listener = keyListenersMap.get(e.keyCode);
      return listener && listener(e);
    };

    document.addEventListener('keydown', keyListener);

    return () => document.removeEventListener('keydown', keyListener);
  }, [open, onClose]);

  const sidebarVariants = {
    hidden: { x: '-100%' },
    visible: { x: 0 },
    exit: { x: '-100%' },
  };

  const siderBar = (
    <>
      {open && (
        <div
          data-testid="background"
          className="alf-background"
          role="button"
          tabIndex={0}
          aria-label="Close side bar"
          onClick={handledClose}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handledClose();
            }
          }}
        />
      )}

      <motion.div
        role="complementary"
        className="alf-sidebar"
        aria-label="Side Bar"
        initial="hidden"
        animate={open ? 'visible' : 'hidden'}
        exit="exit"
        variants={sidebarVariants}
        transition={{ duration: 0.01, ease: 'easeInOut' }}
      >
        <div className="wrapper" ref={sideBarRef} role="navigation">
          <Typography
            type="h2"
            component="h1"
            className="!text-legacy-white"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </Typography>

          {children}
        </div>
      </motion.div>
    </>
  );
  return ReactDOM.createPortal(siderBar, document.body);
};

SideBar.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string,
  onClose: PropTypes.func,
  children: PropTypes.any,
};

export default SideBar;
